import ks from "../ReUsableTS/kBrowserShared";
import { kFormTheme } from "./kFormTheme";
import { kScopedStyles } from "./kScopedStyles";
import type { kModalDialog } from "./kModalDialog";

/**
 * Member bodies of kModalDialog moved out of kModalDialog.ts (size split, 2026-10-20). kModalDialog keeps every
 * member with the same signature as a one-line delegate here; private state is reached by element access
 * (Dlg["_X"]), and the class itself is passed in (Cls) for its static state, so there is no runtime import
 * back to kModalDialog.ts.
 */
export class kModalDialogContent {
    /**
     * Make a hosted kHTMLTabStrip fit the content area, so the dialog does NOT grow a
     * second scrollbar beside the tab pane's own.
     *
     * THE DEFECT THIS FIXES (measured 2026-08-04, reproduced in KUiHarness): a strip is
     * sized with a fixed inline pixel Height. When a consumer picks a number bigger than
     * the dialog's content area - Calendar Browser's booking form used a 530px strip in a
     * 575px dialog, whose inner content measures ~500px - the strip overflows
     * #kDialogInnerContent, which scrolls, WHILE .kHTMLTabsContent inside it is also
     * scrolling its own overflow. Two vertical scrollbars, side by side, for one overflow.
     *
     * Why it is done HERE and imperatively rather than in CSS: `max-height:100%` only
     * resolves against a parent with a definite height, and consumers wrap the strip in
     * their own layout (CB puts it in a kHTMLTable cell), so the percentage is dropped.
     * Measuring after Show() is the only point where the real available height is known.
     *
     * The pane keeps the scrolling, which is the correct owner: it is the sized box.
     */
    // ^ moved with kModalDialog._FitTabStripToContent
    public static _FitTabStripToContent(Dlg: kModalDialog): void {
        if (ks.KErrorMode && ks.KShowingErrorDialog === false) { return; }
        try {
            if (Dlg.InnerContent === null) { return; }
            const Strip: HTMLElement | null =
                Dlg.InnerContent.querySelector(".kHTMLTabStripContainer");
            if (Strip === null) { return; }
            // Shrink by the MEASURED overflow, not to the full content height: the strip
            // usually shares the content area with siblings (CB puts an action row beneath
            // it), so sizing the strip to the whole area still overflows by the siblings.
            const Overflow: number = Dlg.InnerContent.scrollHeight - Dlg.InnerContent.clientHeight;
            if (Overflow <= 0) { return; }
            const NewHeight: number = Strip.offsetHeight - Overflow;
            // Never crush the strip to nothing - if the dialog is genuinely too small for
            // its own content, let the dialog scroll rather than hide the tabs entirely.
            if (NewHeight < 160) { return; }
            Strip.style.height = NewHeight + "px";
            Strip.style.maxHeight = NewHeight + "px";
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogContent._FitTabStripToContent"); }
    }
    /** Paint the title and keep the dialog box's accessible name in sync with it.
     *  aria-labelledby points at TitleDiv while there IS a title; with no title the
     *  reference is dropped so the aria-label fallback is what a screen reader reads
     *  (a labelledby pointing at an empty node yields NO accessible name at all). */
    // ^ moved with kModalDialog._ApplyTitle
    public static _ApplyTitle(Dlg: kModalDialog): void {
        if (ks.KErrorMode && ks.KShowingErrorDialog === false) { return; }
        try {
            if (Dlg.TitleDiv !== null) {
                Dlg.TitleDiv.textContent = Dlg.Title;
                // textContent above REPLACES every child, so the accent dot has to be
                // put back. Show() calls _ApplyTitle, so a caller that sets the accent
                // BEFORE showing would otherwise lose the dot at the moment it appears.
                if (ks.KNotEmptyString(Dlg["_TitleAccentColor"])) {
                    Dlg["_AddTitleDot"](Dlg["_TitleAccentColor"]);
                }
            }
            if (Dlg.DialogContent === null) { return; }
            if (ks.Nz(Dlg.Title) !== "") {
                Dlg.DialogContent.setAttribute("aria-labelledby", Dlg["_TitleId"]);
                Dlg.DialogContent.setAttribute("aria-label", Dlg.Title);
                return;
            }
            Dlg.DialogContent.removeAttribute("aria-labelledby");
            Dlg.DialogContent.setAttribute("aria-label", "Dialog");
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogContent._ApplyTitle"); }
    }
    /** Body of kModalDialog.SetTitle (moved verbatim; the dialog is Dlg). */
    public static SetTitle(Dlg: kModalDialog, NewTitle:string): void {
        if (ks.KErrorMode && ks.KShowingErrorDialog === false) { return; }
        try {
            Dlg.Title = NewTitle;
            Dlg["_ApplyTitle"]();
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogContent.SetTitle"); }
    }
    /** Body of kModalDialog.SetStandardAppTitle (moved verbatim; the dialog is Dlg). */
    public static SetStandardAppTitle(Dlg: kModalDialog): void {
        if (ks.KErrorMode && ks.KShowingErrorDialog === false) { return; }
        try {
            Dlg.SetTitle(ks.KApplication.name + " " + ks.KApplication.version);
            Dlg.SetGreyTitle();
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogContent.SetStandardAppTitle"); }
    }
    /** Body of kModalDialog.SetAccentTitle (moved verbatim; the dialog is Dlg). */
    public static SetAccentTitle(Dlg: kModalDialog, AccentColor?: string): void {
        if (ks.KErrorMode && ks.KShowingErrorDialog === false) { return; }
        try {
            kFormTheme.Install();
            const TitleStyle: CSSStyleDeclaration = Dlg.TitleDiv.style;
            // Pull the bar out to the panel edges, exactly as SetGreyTitle does - the
            // content div carries 16px padding that a title bar must escape.
            TitleStyle.marginLeft = "-16px";
            TitleStyle.marginRight = "-16px";
            TitleStyle.marginTop = "-16px";
            TitleStyle.marginBottom = "12px";
            TitleStyle.minHeight = "28px";
            TitleStyle.paddingTop = "10px";
            TitleStyle.paddingBottom = "10px";
            TitleStyle.paddingLeft = "12px";
            TitleStyle.borderRadius = "8px 8px 0 0";
            Dlg.TitleDiv.classList.add("kDialogTitle--accent");
            if (ks.KNotEmptyString(AccentColor)) {
                Dlg["_TitleAccentColor"] = ks.Nz(AccentColor);
                TitleStyle.borderLeftColor = Dlg["_TitleAccentColor"];
                Dlg["_AddTitleDot"](Dlg["_TitleAccentColor"]);
            }
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogContent.SetAccentTitle"); }
    }
    /** Body of kModalDialog._AddTitleDot (moved verbatim; the dialog is Dlg). */
    public static _AddTitleDot(Dlg: kModalDialog, Color: string): void {
        if (ks.KErrorMode && ks.KShowingErrorDialog === false) { return; }
        try {
            const Existing: HTMLElement | null =
                Dlg.TitleDiv.querySelector(".kDialogTitleDot");
            if (Existing !== null) {
                Existing.style.backgroundColor = Color;
                return;
            }
            Dlg.TitleDiv.insertBefore(kFormTheme.ColorDot(Color), Dlg.TitleDiv.firstChild);
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogContent._AddTitleDot"); }
    }
    /** Body of kModalDialog.SetGreyTitle (moved verbatim; the dialog is Dlg). */
    public static SetGreyTitle(Dlg: kModalDialog): void {
        if (ks.KErrorMode && ks.KShowingErrorDialog === false) { return; }
        try {
            const TitleStyle: CSSStyleDeclaration = Dlg.TitleDiv.style;
            TitleStyle.backgroundColor = "#0a7ea4";
            Dlg.TitleDiv.classList.add("kDialogTitle--bar");
            // The × really does sit on the coloured bar in THIS style, so it goes white here.
            // Its default is dark, for the light-bar and no-bar cases - see the close style.
            Dlg.CancelButtonTopRight.style.color = "white";
            TitleStyle.marginLeft = "-16px";
            TitleStyle.marginRight= "-16px";
            TitleStyle.marginTop = "-16px";
            TitleStyle.color = "white";
            TitleStyle.borderRadius = "8px 8px 0 0";

            TitleStyle.minHeight = "28px";
            TitleStyle.paddingTop = "7px";
            TitleStyle.paddingLeft = "12px";
            TitleStyle.paddingBottom = "7px";
            TitleStyle.marginBottom = "12px";


        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogContent.SetGreyTitle"); }
    }
    /** Body of kModalDialog.SetContentScope (moved verbatim; the dialog is Dlg). */
    public static SetContentScope(Dlg: kModalDialog, Cls: typeof kModalDialog, AppName: string, ClassName: string = ""): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Dlg.InnerContent === null) { return; }
            if (ks.Nz(AppName) !== "") { kScopedStyles.Apply(Dlg.InnerContent, AppName); }
            if (ks.Nz(ClassName) !== "") { Cls["_AddClasses"](Dlg.InnerContent, ClassName); }
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogContent.SetContentScope"); }
    }
    /** Body of kModalDialog.AddContent (moved verbatim; the dialog is Dlg). */
    public static AddContent(Dlg: kModalDialog, NewElem:HTMLElement ): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Dlg.InnerContent.appendChild(NewElem);
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogContent.AddContent"); }
    }
    /** Body of kModalDialog.SetHostStyled (moved verbatim; the dialog is Dlg). */
    public static SetHostStyled(Dlg: kModalDialog, Cls: typeof kModalDialog, ContentClassName: string, ContainerClassName?: string): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Dlg["_HostStyled"] = true;
            if (Dlg.DialogContent !== null) {
                const Content: CSSStyleDeclaration = Dlg.DialogContent.style;
                Content.backgroundColor = "";
                Content.padding = "";
                Content.border = "";
                Content.borderRadius = "";
                Content.boxShadow = "";
                Cls["_AddClasses"](Dlg.DialogContent, ContentClassName);
            }
            if (Dlg.DialogContainer !== null) {
                Dlg.DialogContainer.style.backgroundColor = "";
                if (ContainerClassName !== undefined) {
                    Cls["_AddClasses"](Dlg.DialogContainer, ContainerClassName);
                }
            }
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogContent.SetHostStyled"); }
    }
    /** Body of kModalDialog.SetWidth (moved verbatim; the dialog is Dlg). */
    public static SetWidth(Dlg: kModalDialog): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Dlg.DialogContent && Dlg.DialogContainer){
                if(Dlg.Width === 0){
                    // Host-styled: leave the width to the stylesheet (see SetHostStyled).
                    if (Dlg["_HostStyled"] === true) { return; }
                    Dlg.DialogContent.style.width ="80%"; /* Could be more or less, depending on screen size */
                }else{

                    Dlg.DialogContent.style.width = Dlg.Width.toString() + "px";
                }
            }
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogContent.SetWidth"); }
    }
    /** Body of kModalDialog.SetHeigth (moved verbatim; the dialog is Dlg). */
    public static SetHeigth(Dlg: kModalDialog): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Dlg.Height === 0){
                // Host-styled: leave the height to the stylesheet (see SetHostStyled).
                if (Dlg["_HostStyled"] === true) { return; }
                if (Dlg.AutoHeight === true) {
                    // Content height, capped: the inner content already scrolls (flex:1 +
                    // overflow auto), so a long receipt still scrolls instead of growing off-screen.
                    Dlg.DialogContent.style.height = "auto";
                    Dlg.DialogContent.style.maxHeight = "80%";
                    return;
                }
                Dlg.DialogContent.style.height ="80%"; /* Could be more or less, depending on screen size */
            }else{
                Dlg.DialogContent.style.height = Dlg.Height.toString() + "px";
            }
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogContent.SetHeigth"); }
    }
    /** Body of kModalDialog.SetContent (moved verbatim; the dialog is Dlg). */
    public static SetContent(Dlg: kModalDialog, NewHTML:string): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Dlg.InnerContent.innerHTML = NewHTML;
            Dlg.ContentHTML = NewHTML;
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogContent.SetContent"); }
    }
    /** Body of kModalDialog.SetContentElement (moved verbatim; the dialog is Dlg). */
    public static SetContentElement(Dlg: kModalDialog, NewContent:Element): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Dlg.InnerContent.innerHTML = "";
            Dlg.InnerContent.appendChild(NewContent);
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogContent.SetContentElement"); }
    }
}
